// `evidence/source` — the auto-evidence config WRITE, carved from
// `src/hooks/mutation/useMutateEvidenceConfig.ts` (now a pure re-export shim).
//
// NO SEVERANCE WAS NEEDED, and the census said the opposite until this PR — it
// listed this hook as deferred, `direct: false`. That was a TRANSITIVE deferral
// and nothing more: the file was held only by its import of the app-side
// `src/lib/api/transcript-chips.ts`, which carved in this same PR. The hook
// itself was already headless — no toast, no store read, no flag, no intl, no
// atom — and it resolves its cache client with `useQueryClient()` INSIDE the
// hook body, exactly as it did before the move, so it needs nothing from the
// `configureEvidenceData` seam either. That is why the old path dissolves to a
// re-export rather than staying bodied.
//
// The optimistic block is moved BYTE-FOR-BYTE, and the parts that look like
// tidying opportunities are load-bearing:
//
//   * The ID-PRESERVING MATCH (`team_shortcut_id` first, then label+query) is
//     what stops `AnimatePresence` re-animating chips that did not change. A
//     `map` that minted a fresh id for every pin would be simpler and would
//     visibly flash the whole rail on every edit.
//   * `category: 'plan'` on a newly minted optimistic pin is a placeholder the
//     server overwrites on the next read. It is not a default worth "fixing" —
//     the alternative is `null`, which renders a different chip.
//   * The three explicit `?? old?.… ?? []` fallbacks exist because
//     `AutoEvidenceConfigUpdate` is a PARTIAL: spreading it over the previous
//     entry would otherwise write `undefined` into array fields the readers
//     iterate.
//   * `onSettled` invalidates unconditionally, so a rolled-back error still
//     refetches. Moving the invalidation to `onSuccess` would leave a failed
//     write showing the rolled-back value until something else evicted it.
//
// `logger` crosses as the SAME SINK: `@/utils/logger/logger` is a pure runtime
// re-export of `@heidi/runtime/logger` (Wave 1 · P1).
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { logger } from '@heidi/runtime/logger';

import {
  DEFAULT_EVIDENCE_CONFIG,
  GET_EVIDENCE_CONFIG,
  UPDATE_EVIDENCE_CONFIG,
  type AutoEvidenceConfig,
  type AutoEvidenceConfigUpdate,
  type EffectivePinnedQuery,
} from '../../contracts';
import { transcriptChipsApi } from '../api/transcript-chips';

/**
 * Hook to update user's Evidence configuration
 *
 * Supports partial updates - only provided fields will be updated.
 * Uses optimistic updates for instant UI feedback.
 */
export function useMutateEvidenceConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_EVIDENCE_CONFIG],
    mutationFn: async (data: AutoEvidenceConfigUpdate) => {
      return await transcriptChipsApi.updateConfig(data);
    },
    onMutate: async (newConfig) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [GET_EVIDENCE_CONFIG] });

      // Snapshot the previous value
      const previousConfig = queryClient.getQueryData<AutoEvidenceConfig>([
        GET_EVIDENCE_CONFIG,
      ]);

      // Build optimistic pinned_queries list
      let optimisticPinnedQueries: EffectivePinnedQuery[] =
        previousConfig?.pinned_queries ?? [];

      if (newConfig.pinned_queries) {
        // Keep existing defaults (not editable via pinned_queries updates)
        const existingDefaults = optimisticPinnedQueries.filter((pq) => pq.is_default);
        const existingUserPins = optimisticPinnedQueries.filter((pq) => !pq.is_default);

        // Match new pinned_queries against existing custom pins to preserve IDs.
        // This prevents AnimatePresence from re-animating chips that didn't change.
        const newUserPins: EffectivePinnedQuery[] = newConfig.pinned_queries.map(
          (pq, index) => {
            const existing = existingUserPins.find(
              (ep) =>
                (pq.team_shortcut_id && ep.team_shortcut_id === pq.team_shortcut_id) ||
                (ep.label === pq.label && ep.query === pq.query)
            );
            if (existing) {
              return {
                ...existing,
                label: pq.label,
                query: pq.query,
                description:
                  pq.description !== undefined
                    ? pq.description || undefined
                    : existing.description,
                team_shortcut_id: pq.team_shortcut_id ?? existing.team_shortcut_id,
              };
            }
            return {
              id: `optimistic-${Date.now()}-${index}`,
              label: pq.label,
              query: pq.query,
              category: 'plan',
              is_default: false,
              description: pq.description || undefined,
              team_shortcut_id: pq.team_shortcut_id,
              origin: pq.origin ?? 'custom',
            };
          }
        );

        optimisticPinnedQueries = [...existingDefaults, ...newUserPins];
      }

      queryClient.setQueryData<AutoEvidenceConfig>([GET_EVIDENCE_CONFIG], (old) => ({
        ...(old ?? DEFAULT_EVIDENCE_CONFIG),
        ...newConfig,
        // Handle nested arrays properly
        enabled_categories: newConfig.enabled_categories ?? old?.enabled_categories ?? [],
        // Optimistically update pinned_queries
        pinned_queries: optimisticPinnedQueries,
        dismissed_default_ids:
          newConfig.dismissed_default_ids ?? old?.dismissed_default_ids ?? [],
      }));

      // Return context with the previous value
      return { previousConfig };
    },
    onError: (error, _newConfig, context) => {
      logger.error('Failed to update evidence config:', error);
      // Rollback to previous value on error
      if (context?.previousConfig) {
        queryClient.setQueryData([GET_EVIDENCE_CONFIG], context.previousConfig);
      }
    },
    onSettled: () => {
      // Refetch after mutation settles to ensure consistency
      void queryClient.invalidateQueries({ queryKey: [GET_EVIDENCE_CONFIG] });
    },
  });
}
